import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Flex, Progress, Statistic, Typography } from 'antd';
import classNames from 'classnames';
import { useGetClientDebt } from 'api/clients/use-get-client-debt.ts';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import { numberFormatter } from 'shared/utils/numbers/formatter.ts';
import { round100 } from 'shared/utils/numbers/round.ts';
import { CURRENCY_SIGN, ECurrency } from 'types/currencies.ts';
import styles from './client-credit-info.module.scss';
import { DebtBreakdownPopover } from './components/debt-breakdown-popover';
import type { IClientCreditInfoProps } from './types.ts';
import { calculatePercent } from './utils.ts';

const { Text } = Typography;
const EUR_SIGN = CURRENCY_SIGN[ECurrency.EUR];

export const ClientCreditInfoComponent: React.FC<IClientCreditInfoProps> = (props) => {
  const { clientId, creditLimit } = props;
  const { t } = useTranslation();
  const { data: debt, isLoading } = useGetClientDebt(clientId);

  const hasCreditLimit = creditLimit !== null && creditLimit !== undefined;
  const hasDebt = debt !== undefined;
  const hasCreditData = hasCreditLimit && hasDebt;
  const available = hasCreditData ? round100(creditLimit - debt.totalDebt) : null;
  const availableAmount = Math.abs(available ?? 0);
  const isExceeded = available !== null && available < 0;

  const progressPercent = hasCreditData ? calculatePercent(debt.totalDebt, creditLimit) : 0;

  const creditLimitSharePercent = isExceeded && hasCreditData ? calculatePercent(creditLimit, debt.totalDebt) : 0;

  return (
    <Card className={styles.card} size='small' loading={isLoading}>
      <Flex gap='large'>
        <Statistic
          className={styles.statistic}
          title={t('client:credit_limit')}
          value={hasCreditLimit ? creditLimit : t('client:not_set')}
          suffix={hasCreditLimit ? EUR_SIGN : undefined}
          valueStyle={hasCreditLimit ? undefined : { color: 'var(--gray-text)' }}
        />
        <DebtBreakdownPopover debt={debt}>
          <div className={styles.statistic}>
            <Statistic
              title={t('client:outstanding_debt')}
              value={hasDebt ? debt.totalDebt : '—'}
              suffix={hasDebt ? EUR_SIGN : undefined}
              valueStyle={isExceeded ? { color: 'var(--error)' } : undefined}
            />
          </div>
        </DebtBreakdownPopover>
      </Flex>

      {hasCreditData ? (
        <>
          <Progress
            className={classNames(styles.progress, { [styles.exceededProgress]: isExceeded })}
            percent={progressPercent}
            style={
              {
                '--credit-limit-share': `${creditLimitSharePercent}%`,
              } as React.CSSProperties
            }
            showInfo={false}
          />
          <Flex justify='space-between'>
            <Text type='secondary'>{isExceeded ? t('client:exceeded_by') : t('client:available')}</Text>
            <Text type={isExceeded ? 'danger' : 'success'} strong>
              {numberFormatter(availableAmount)} {EUR_SIGN}
            </Text>
          </Flex>
        </>
      ) : null}
    </Card>
  );
};

export const ClientCreditInfo = withRoleGuard(ClientCreditInfoComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.transport_manager,
  UserRole.customer_manager,
  UserRole.accountant,
  UserRole.mechanic,
]);
